import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, MessageSquare, Bot, Plug, Workflow, FileText, Users,
  ArrowRight, Send, Star, ChevronDown, Play
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';
import { LOGOS, TESTIMONIALS, SHOWCASE_IMAGES } from '../mock/mock';
import { PricingCard } from '../components/PricingCard';

const FEATURE_ICONS = [MessageSquare, Bot, Plug, Workflow, FileText, Users];

const Section = ({ children, className = '', id }) => (
  <section id={id} className={`max-w-7xl mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const Landing = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');

  const startChat = () => {
    if (draft.trim()) navigate('/chat', { state: { initial: draft.trim() } });
    else navigate('/chat');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden pt-28 pb-20 sm:pt-36 sm:pb-28">
        <div className="absolute inset-0 aurra-hero-glow pointer-events-none" />
        <div className="absolute inset-0 aurra-grid-bg opacity-[0.35] [mask-image:radial-gradient(70%_60%_at_50%_0%,black,transparent)] pointer-events-none" />
        <Section className="relative">
          <div className="max-w-3xl mx-auto text-center">
            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs sm:text-sm font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors animate-fade-up shadow-sm"
            >
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              {t.hero.badge}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <h1 className="mt-6 font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05] animate-fade-up" style={{ animationDelay: '60ms' }}>
              {t.hero.title1}<br />
              <span className="aurra-gradient-text">{t.hero.title2}</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: '120ms' }}>
              {t.hero.subtitle}
            </p>

            {/* Hero chat input */}
            <div className="mt-9 max-w-xl mx-auto animate-fade-up" style={{ animationDelay: '180ms' }}>
              <div className="flex items-center gap-2 rounded-2xl border border-border bg-card p-2 pl-4 shadow-lg shadow-primary/5 focus-within:border-primary/50 transition-colors">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && startChat()}
                  placeholder={t.hero.placeholder}
                  className="flex-1 bg-transparent outline-none text-sm sm:text-base placeholder:text-muted-foreground"
                />
                <Button onClick={startChat} size="icon" className="rounded-xl h-10 w-10 shrink-0">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <Button onClick={startChat} className="rounded-xl font-semibold px-6">
                  {t.hero.cta}
                </Button>
                <Button onClick={() => navigate('/agents')} variant="outline" className="rounded-xl font-semibold px-6">
                  <Play className="h-4 w-4 mr-1.5" /> {t.hero.cta2}
                </Button>
              </div>
            </div>
          </div>

          {/* Trusted logos */}
          <div className="mt-16 sm:mt-20 animate-fade-up" style={{ animationDelay: '240ms' }}>
            <p className="text-center text-xs uppercase tracking-widest text-muted-foreground mb-5">{t.trusted}</p>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
              {LOGOS.map((l) => (
                <span key={l} className="font-display text-lg font-semibold text-muted-foreground">{l}</span>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* Features */}
      <Section id="features" className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t.features.title}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.features.subtitle}</p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.features.items.map((f, i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <div key={f.title} className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1">
                <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Showcase */}
      <Section className="pb-8 sm:pb-16">
        <div className="rounded-3xl border border-border overflow-hidden bg-secondary/40 relative">
          <div className="grid lg:grid-cols-2 items-center">
            <div className="p-8 sm:p-12">
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t.how.title}</h3>
              <p className="mt-2 text-muted-foreground">{t.how.subtitle}</p>
              <div className="mt-8 space-y-6">
                {t.how.steps.map((s, i) => (
                  <div key={s.title} className="flex gap-4">
                    <div className="h-9 w-9 shrink-0 rounded-full bg-primary text-primary-foreground font-display font-bold flex items-center justify-center text-sm">{i + 1}</div>
                    <div>
                      <h4 className="font-display font-semibold">{s.title}</h4>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-64 lg:h-full min-h-[320px]">
              <img src={SHOWCASE_IMAGES.workspace} alt="workspace" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-secondary/60 to-transparent" />
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="py-16 sm:py-24">
        <div className="grid md:grid-cols-2 gap-5">
          {TESTIMONIALS.map((tm) => (
            <div key={tm.name} className="rounded-2xl border border-border bg-card p-7">
              <div className="flex gap-0.5 text-primary mb-4">
                {['a', 'b', 'c', 'd', 'e'].map((k) => <Star key={`${tm.name}-star-${k}`} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-base leading-relaxed">&ldquo;{tm.quote}&rdquo;</p>
              <div className="mt-6 flex items-center gap-3">
                <img src={tm.avatar} alt={tm.name} className="h-11 w-11 rounded-full object-cover" />
                <div>
                  <p className="font-semibold text-sm">{tm.name}</p>
                  <p className="text-xs text-muted-foreground">{tm.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" className="py-16 sm:py-24">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t.pricing.title}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.pricing.subtitle}</p>
        </div>
        <div className="mt-12 max-w-md mx-auto">
          <PricingCard />
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-16 sm:py-24">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-center">{t.faq.title}</h2>
          <Accordion type="single" collapsible className="mt-10">
            {t.faq.items.map((item, i) => (
              <AccordionItem key={item.q} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left font-display font-semibold hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CTA */}
      <Section className="pb-24">
        <div className="relative rounded-3xl bg-primary px-8 py-16 text-center overflow-hidden">
          <div className="absolute inset-0 aurra-grid-bg opacity-10" />
          <div className="relative">
            <Sparkles className="h-8 w-8 mx-auto text-primary-foreground/90" />
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-primary-foreground">{t.cta.title}</h2>
            <p className="mt-3 text-primary-foreground/80 max-w-lg mx-auto">{t.cta.subtitle}</p>
            <Button onClick={() => navigate('/chat')} variant="secondary" className="mt-8 rounded-xl font-semibold px-8 h-12 text-base">
              {t.cta.button} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </Section>

      <Footer />
    </div>
  );
};

export default Landing;
